import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import {
  UserPlus, Users, ShieldCheck, ShieldOff, Trash2,
  RotateCcw, ChevronUp, Crown, UserMinus, Settings2, Search, X, Infinity as InfinityIcon,
} from "lucide-react";
import { Button } from "../components/ui/button";
import AddSeatButton from "../components/AddSeatButton";

function StatusBadge({ enabled }) {
  return enabled ? (
    <span className="font-mono text-[9px] px-2 py-0.5 border bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30 tracking-[0.1em] font-bold">ACTIVE</span>
  ) : (
    <span className="font-mono text-[9px] px-2 py-0.5 border bg-[#E11D48]/10 text-[#E11D48] border-[#E11D48]/30 tracking-[0.1em] font-bold">DISABLED</span>
  );
}

function RoleBadge({ subRole }) {
  return subRole === "admin" ? (
    <span className="font-mono text-[9px] px-2 py-0.5 border bg-[#0025D8]/10 text-[#0025D8] border-[#0025D8]/30 tracking-[0.1em] font-bold flex items-center gap-1">
      <Crown className="w-2.5 h-2.5" />ADMIN
    </span>
  ) : (
    <span className="font-mono text-[9px] px-2 py-0.5 border bg-secondary text-muted-foreground border-border tracking-[0.1em]">EMPLOYEE</span>
  );
}

function ConfirmDialog({ message, confirmLabel, danger, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl border border-border p-6 max-w-sm w-full shadow-2xl">
        <p className="text-sm mb-5">{message}</p>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-sm rounded-xl border border-border hover:bg-secondary transition-colors">Cancel</button>
          <button onClick={onConfirm} className={`px-4 py-2 text-sm font-bold text-white transition-colors ${danger ? "bg-[#E11D48] hover:bg-[#C01038]" : "bg-[#0025D8] hover:bg-[#001CB3]"}`}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

function AddEmployeeModal({ onClose, onAdded }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim() || !email.trim()) { toast.error("Name and email are required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/company/employees", { name: name.trim(), email: email.trim() });
      toast.success(`Invite sent to ${data.email}`);
      onAdded(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create employee");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl border border-border p-6 max-w-sm w-full shadow-2xl">
        <h2 className="font-semibold text-sm mb-4">Add Employee</h2>
        <div className="space-y-3 mb-5">
          <div>
            <label className="font-mono text-[10px] tracking-[0.1em] text-muted-foreground">FULL NAME</label>
            <input value={name} onChange={e => setName(e.target.value)} className="w-full mt-1 rounded-xl border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#0025D8]" placeholder="Jane Smith" />
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-[0.1em] text-muted-foreground">WORK EMAIL</label>
            <input value={email} onChange={e => setEmail(e.target.value)} type="email" className="w-full mt-1 rounded-xl border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#0025D8]" placeholder="jane@company.com" />
          </div>
        </div>
        <p className="font-mono text-[10px] text-muted-foreground mb-4">A temporary password will be generated and emailed to the employee automatically.</p>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-border hover:bg-secondary transition-colors">Cancel</button>
          <Button onClick={submit} disabled={saving} className="rounded-xl bg-[#0025D8] hover:bg-[#001CB3]">
            {saving ? "Sending…" : "Send Invite"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ShareSettings({ onClose }) {
  const [settings, setSettings] = useState({ share_roles_enabled: false, share_candidates_enabled: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/company/settings").then(({ data }) => setSettings(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/company/settings", settings);
      toast.success("Settings saved — reloading to apply changes");
      onClose();
      window.location.reload();
    } catch { toast.error("Could not save settings"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl border border-border p-6 max-w-sm w-full shadow-2xl">
        <h2 className="font-semibold text-sm mb-4">Employee Visibility Settings</h2>
        {loading ? <div className="font-mono text-xs text-muted-foreground">Loading…</div> : (
          <div className="space-y-4 mb-5">
            <label className="flex items-center justify-between gap-4 cursor-pointer">
              <div>
                <div className="text-sm font-medium">Share job roles</div>
                <div className="font-mono text-[10px] text-muted-foreground mt-0.5">Employees can see all job roles in the company</div>
              </div>
              <input type="checkbox" checked={settings.share_roles_enabled} onChange={e => setSettings(s => ({ ...s, share_roles_enabled: e.target.checked }))} className="w-4 h-4" />
            </label>
            <label className="flex items-center justify-between gap-4 cursor-pointer">
              <div>
                <div className="text-sm font-medium">Share candidate list</div>
                <div className="font-mono text-[10px] text-muted-foreground mt-0.5">Employees can see all candidates across the company</div>
              </div>
              <input type="checkbox" checked={settings.share_candidates_enabled} onChange={e => setSettings(s => ({ ...s, share_candidates_enabled: e.target.checked }))} className="w-4 h-4" />
            </label>
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-border hover:bg-secondary transition-colors">Cancel</button>
          <Button onClick={save} disabled={saving || loading} className="rounded-xl bg-[#0025D8] hover:bg-[#001CB3]">{saving ? "Saving…" : "Save"}</Button>
        </div>
      </div>
    </div>
  );
}

export default function CompanyEmployees() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && user.role === "company_employee") {
      toast.error("Only company admins can manage employees");
      navigate("/company/roles", { replace: true });
    }
  }, [user, navigate]);
  const [showAdd, setShowAdd] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [actingId, setActingId] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/company/employees");
      setEmployees(data.employees || []);
    } catch { toast.error("Could not load employees"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const act = async (action, employeeId, label) => {
    setActingId(employeeId);
    try {
      await api.patch(`/company/employees/${employeeId}/${action}`);
      toast.success(`Employee ${label}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not ${label} employee`);
    } finally { setActingId(null); }
  };

  const resetPassword = async (employeeId) => {
    setActingId(employeeId);
    try {
      await api.post(`/company/employees/${employeeId}/reset-password`);
      toast.success("Password reset email sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password");
    } finally { setActingId(null); }
  };

  const deleteEmployee = async (employeeId) => {
    setActingId(employeeId);
    try {
      await api.delete(`/company/employees/${employeeId}`);
      toast.success("Employee removed");
      setEmployees(prev => prev.filter(e => e.user_id !== employeeId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove employee");
    } finally { setActingId(null); setConfirm(null); }
  };

  const isLoading = (id) => actingId === id;

  // ── Seat accounting ──
  // effective_seat_limit = plan base seats + paid add-on seats. null = unlimited (grandfathered).
  // Seats used = employees + 1 (the admin). Mirror the backend's create_employee cap exactly.
  const billing = user?.company_billing || {};
  const seatLimit = billing.effective_seat_limit;      // null => unlimited
  const unlimitedSeats = seatLimit == null;
  const basePlanSeats = billing.seat_limit;            // base tier seats
  const paidSeats = billing.addon_seats || 0;
  const seatsUsed = employees.length + 1;              // live list + admin
  const seatsAvailable = unlimitedSeats ? Infinity : Math.max(0, seatLimit - seatsUsed);
  const atCapacity = !unlimitedSeats && seatsAvailable <= 0;
  const overBy = unlimitedSeats ? 0 : Math.max(0, seatsUsed - seatLimit);   // >0 = over the plan limit
  const isOver = overBy > 0;
  // Seats to buy before ONE more member fits: limit must exceed current usage.
  const seatsToBuyForOneMore = unlimitedSeats ? 0 : Math.max(1, seatsUsed - seatLimit + 1);
  const seatPct = unlimitedSeats ? 0 : Math.min(100, Math.round((seatsUsed / seatLimit) * 100));

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      {/* Header */}
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight text-[#0F172A]">Employees</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage team members under your company account</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowSettings(true)} className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-xl border border-border hover:bg-secondary transition-colors font-mono">
            <Settings2 className="w-3.5 h-3.5" /> Settings
          </button>
          {atCapacity && (
            <AddSeatButton
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border border-[#0025D8]/30 text-[#0025D8] hover:bg-[#0025D8]/5 transition-colors disabled:opacity-60" />
          )}
          <Button
            onClick={() => setShowAdd(true)}
            disabled={atCapacity}
            title={atCapacity
              ? (isOver
                  ? `You're over your plan limit — buy ${seatsToBuyForOneMore} more seats (or increase your plan) to add another member`
                  : "All seats are in use — buy an extra seat to invite more")
              : undefined}
            className="rounded-xl bg-[#0025D8] hover:bg-[#001CB3] disabled:opacity-50 disabled:cursor-not-allowed">
            <UserPlus className="w-4 h-4 mr-2" /> Add Employee
          </Button>
        </div>
      </div>

      {/* Seat usage */}
      <div className={`rounded-xl border px-4 py-3.5 mb-6 ${atCapacity ? "border-[#E11D48]/40 bg-[#E11D48]/5" : "border-border bg-white"}`}>
        {unlimitedSeats ? (
          <div className="flex items-center gap-2 text-sm">
            <InfinityIcon className="w-4 h-4 text-[#0025D8]" />
            <span className="font-semibold text-[#0F172A]">Unlimited seats</span>
            <span className="text-muted-foreground">— add as many team members as you need.</span>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <Users className="w-3.5 h-3.5" /> SEAT USAGE
              </div>
              <div className="text-sm">
                <span className={`font-mono font-bold ${atCapacity ? "text-[#E11D48]" : "text-[#0F172A]"}`}>
                  {seatsUsed} of {seatLimit} used
                </span>
                <span className="text-muted-foreground">
                  {isOver ? ` · ${overBy} over limit` : ` · ${seatsAvailable} available`}
                </span>
              </div>
            </div>
            <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
              <div className={`h-full rounded-full ${atCapacity ? "bg-[#E11D48]" : "bg-[#0025D8]"}`} style={{ width: `${seatPct}%` }} />
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap mt-2">
              <p className="font-mono text-[10px] text-muted-foreground">
                {basePlanSeats} plan seat{basePlanSeats !== 1 ? "s" : ""}
                {paidSeats > 0 ? ` + ${paidSeats} paid add-on${paidSeats !== 1 ? "s" : ""}` : ""}
                {" · admin uses 1 seat"}
              </p>
              {atCapacity && (
                <p className="text-[11px] font-semibold text-[#E11D48]">
                  {isOver
                    ? `You're ${overBy} seat${overBy !== 1 ? "s" : ""} over your plan. Buy ${seatsToBuyForOneMore} more to add another member, or remove members.`
                    : `All seats in use — buy an extra seat to invite more.`}
                </p>
              )}
            </div>
          </>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
        {[
          { label: "Total employees", value: employees.length },
          { label: "Active", value: employees.filter(e => e.account_enabled).length },
          { label: "Admins", value: employees.filter(e => e.sub_role === "admin").length },
        ].map(s => (
          <div key={s.label} className="rounded-xl border border-border p-4">
            <div className="font-mono text-[10px] tracking-[0.1em] text-muted-foreground mb-1">{s.label.toUpperCase()}</div>
            <div className="text-2xl font-bold">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      {employees.length > 0 && (
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full rounded-xl border border-border pl-8 pr-8 py-2 text-sm focus:outline-none focus:border-[#0025D8] transition-all font-mono text-[11px]"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {search && (
            <span className="font-mono text-[10px] text-muted-foreground">
              {employees.filter(e => e.name?.toLowerCase().includes(search.toLowerCase()) || e.email?.toLowerCase().includes(search.toLowerCase())).length} result{employees.filter(e => e.name?.toLowerCase().includes(search.toLowerCase()) || e.email?.toLowerCase().includes(search.toLowerCase())).length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="font-mono text-xs text-muted-foreground">Loading employees…</div>
      ) : employees.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center">
          <Users className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="font-mono text-[10px] tracking-[0.1em] text-muted-foreground">NO EMPLOYEES YET</p>
          <p className="text-sm text-muted-foreground mt-1">Add your first team member to get started.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/40">
                <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.1em] text-muted-foreground font-normal">NAME</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.1em] text-muted-foreground font-normal">EMAIL</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.1em] text-muted-foreground font-normal">ROLE</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.1em] text-muted-foreground font-normal">STATUS</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.1em] text-muted-foreground font-normal">ADDED</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {employees.filter(e => !search || e.name?.toLowerCase().includes(search.toLowerCase()) || e.email?.toLowerCase().includes(search.toLowerCase())).map(emp => (
                <tr key={emp.user_id} className="border-b border-border last:border-0 hover:bg-secondary/20 transition-colors">
                  <td className="px-4 py-3 font-medium">
                    {emp.name}
                    {emp.requires_password_change && (
                      <button
                        onClick={() => setConfirm({ action: "resend", id: emp.user_id, name: emp.name })}
                        title="Resend invite email"
                        className="ml-2 font-mono text-[9px] text-[#F59E0B] border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-1.5 py-0.5 hover:bg-[#F59E0B]/20 transition-colors cursor-pointer"
                      >
                        PENDING SETUP
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">{emp.email}</td>
                  <td className="px-4 py-3"><RoleBadge subRole={emp.sub_role} /></td>
                  <td className="px-4 py-3"><StatusBadge enabled={emp.account_enabled} /></td>
                  <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">
                    {emp.created_at ? new Date(emp.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {emp.sub_role !== "admin" && (
                      <div className="flex items-center gap-2 justify-end flex-wrap">
                        {emp.account_enabled ? (
                          <button onClick={() => setConfirm({ action: "disable", id: emp.user_id, name: emp.name })}
                            disabled={isLoading(emp.user_id)}
                            title="Disable account"
                            className="text-muted-foreground hover:text-[#E11D48] transition-colors disabled:opacity-40">
                            <ShieldOff className="w-3.5 h-3.5" />
                          </button>
                        ) : (
                          <button onClick={() => act("enable", emp.user_id, "enabled")}
                            disabled={isLoading(emp.user_id)}
                            title="Enable account"
                            className="text-muted-foreground hover:text-[#10B981] transition-colors disabled:opacity-40">
                            <ShieldCheck className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button onClick={() => setConfirm({ action: "promote", id: emp.user_id, name: emp.name })}
                          disabled={isLoading(emp.user_id)}
                          title="Promote to admin"
                          className="text-muted-foreground hover:text-[#0025D8] transition-colors disabled:opacity-40">
                          <Crown className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => emp.account_enabled && resetPassword(emp.user_id)}
                          disabled={isLoading(emp.user_id) || !emp.account_enabled}
                          title={emp.account_enabled ? "Reset password" : "Enable account first to reset password"}
                          className="text-muted-foreground hover:text-[#F59E0B] transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                          <RotateCcw className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setConfirm({ action: "delete", id: emp.user_id, name: emp.name })}
                          disabled={isLoading(emp.user_id)}
                          title="Remove employee"
                          className="text-muted-foreground hover:text-[#E11D48] transition-colors disabled:opacity-40">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                    {emp.sub_role === "admin" && emp.user_id !== user?.user_id && (
                      <button onClick={() => setConfirm({ action: "demote", id: emp.user_id, name: emp.name })}
                        disabled={isLoading(emp.user_id)}
                        title="Demote to employee"
                        className="text-muted-foreground hover:text-[#F59E0B] transition-colors disabled:opacity-40 flex items-center gap-1 text-xs font-mono">
                        <UserMinus className="w-3.5 h-3.5" /> Demote
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showAdd && <AddEmployeeModal onClose={() => setShowAdd(false)} onAdded={(e) => setEmployees(prev => [...prev, e])} />}
      {showSettings && <ShareSettings onClose={() => setShowSettings(false)} />}
      {confirm?.action === "resend" && (
        <ConfirmDialog
          message={`Resend invite to ${confirm.name}? A new temporary password will be emailed to them.`}
          confirmLabel="Resend"
          onConfirm={() => { resetPassword(confirm.id); setConfirm(null); }}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm?.action === "promote" && (
        <ConfirmDialog
          message={`Promote ${confirm.name} to admin? They will gain full company access including employee management.`}
          confirmLabel="Promote"
          onConfirm={() => { act("promote", confirm.id, "promoted to admin"); setConfirm(null); }}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm?.action === "demote" && (
        <ConfirmDialog
          message={`Demote ${confirm.name} to employee? They will lose admin access immediately.`}
          confirmLabel="Demote"
          onConfirm={() => { act("demote", confirm.id, "demoted to employee"); setConfirm(null); }}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm?.action === "disable" && (
        <ConfirmDialog
          danger
          message={`Disable ${confirm.name}'s account? They will be immediately logged out and cannot log in until re-enabled.`}
          confirmLabel="Disable"
          onConfirm={() => { act("disable", confirm.id, "disabled"); setConfirm(null); }}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm?.action === "delete" && (
        <ConfirmDialog
          danger
          message={`Remove ${confirm.name}? Their roles and candidates will be kept as orphaned records visible to admins. This cannot be undone.`}
          confirmLabel="Remove"
          onConfirm={() => deleteEmployee(confirm.id)}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
